import React from 'react';
import { WebJsonField } from '../shared/WebJsonField.js';

export interface DataItem {
  json?: unknown;
  [key: string]: unknown;
}

export interface DataItemsViewProps {
  items: Array<DataItem | unknown>;
  /**
   * Override hint to force list/grid rendering even for a single item — item
   * count is still the primary single-vs-multi decider, per the prompt's own
   * "more than 1 items -> list, 1 item -> form" description.
   */
  isList?: boolean;
}

function unwrap(item: DataItem | unknown): unknown {
  return item && typeof item === 'object' && 'json' in (item as Record<string, unknown>)
    ? (item as DataItem).json
    : item;
}

/**
 * Thin wrapper around the existing WebJsonField — not a new renderer.
 * WebJsonField already does the ">1 item" case for free via its array
 * handling (collapsible "Item N" panels with a count badge); this only
 * unwraps the {json: ...} item shape and decides single-item vs. list.
 *
 * Note: the "flat, no-panel" single-item case only applies when the
 * unwrapped value is an object — WebJsonField still wraps a root-level
 * array value in its count-badge panel regardless of label.
 */
export const DataItemsView: React.FC<DataItemsViewProps> = ({ items, isList }) => {
  const unwrapped = items.map(unwrap);

  if (unwrapped.length === 0) {
    return (
      <div className="wjf-prop">
        <span className="wjf-prop__value wjf-null">No data</span>
      </div>
    );
  }

  if (unwrapped.length === 1 && !isList) {
    return <WebJsonField value={unwrapped[0]} />;
  }

  return <WebJsonField value={unwrapped} />;
};
